import { useState } from "react";
import { Panel } from "@/components/dash/primitives";
import { ChartTip } from "@/components/dash/chartkit";
import { ChevronDown, Table as TableIcon } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const BBL = 6.35;

// Two-tier x-axis tick: month on top, year grouped (bold) under each January with a divider.
export function MonthYearTick({ x, y, payload, index }) {
  const raw = String(payload?.value ?? "").replace("'", "").trim();
  const [mon, yy] = raw.split(/\s+/);
  const yearStart = mon === "Jan";
  const full = yy ? (yy.length === 2 ? `20${yy}` : yy) : "";
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fontSize={10} fill="#64748b">{mon}</text>
      {yearStart && <text x={2} y={0} dy={30} textAnchor="start" fontSize={12} fontWeight={700} fill="#334155">{full}</text>}
      {yearStart && index !== 0 && <line x1={0} y1={-4} x2={0} y2={22} stroke="#cbd5e1" strokeWidth={1} />}
    </g>
  );
}

const PORTS = [
  { key: "Rotterdam", label: "Rotterdam (Europe)", color: "#1d3557" },
  { key: "Singapore", label: "Singapore (Asia hub)", color: "#e8a33d" },
  { key: "Houston", label: "Houston (US Gulf)", color: "#2a9d8f" },
];

export function BunkerChart({ data }) {
  const [unit, setUnit] = useState("barrel"); // 'tonne' | 'barrel'
  const [visible, setVisible] = useState({ Rotterdam: true, Singapore: true, Houston: true });
  const [showSgp, setShowSgp] = useState(false);
  const sgpTable = data.bunker_singapore_table;

  const conv = (v) => (v == null ? null : (unit === "barrel" ? v / BBL : v));
  const rows = data.bunker_labels.map((l, i) => ({
    label: l,
    Rotterdam: conv(data.bunker_rotterdam[i]),
    Singapore: conv(data.bunker_singapore[i]),
    Houston: conv(data.bunker_houston[i]),
    RotterdamF: conv((data.bunker_rotterdam_forecast || [])[i]),
    SingaporeF: conv((data.bunker_singapore_forecast || [])[i]),
    HoustonF: conv((data.bunker_houston_forecast || [])[i]),
  }));
  const hasForecast = { Rotterdam: !!data.bunker_rotterdam_forecast, Singapore: !!data.bunker_singapore_forecast, Houston: !!data.bunker_houston_forecast };

  const fmt = (v) =>
    unit === "barrel"
      ? `US$${Math.round(v)}/bbl  (≈ US$${Math.round(v * BBL)}/t)`
      : `US$${Math.round(v)}/t  (≈ US$${Math.round(v / BBL)}/bbl)`;

  const num1 = (v) => (v == null ? "" : v);
  const bbl1 = (v) => (v == null ? "" : (v / BBL).toFixed(1));
  const csv = {
    name: "Bunker prices",
    columns: ["Month", "Rotterdam $/t", "Singapore $/t", "Houston $/t", "Singapore forecast $/t"],
    rows: data.bunker_labels.map((l, i) => [
      l, num1(data.bunker_rotterdam[i]), num1(data.bunker_singapore[i]), num1(data.bunker_houston[i]),
      num1((data.bunker_singapore_forecast || [])[i]),
    ]),
  };

  return (
    <Panel
      title="Bunker fuel prices by port — actuals Jan–Sep 2026, forecast to Sep 2027"
      subtitle={`VLSFO 0.5% — ${unit === "barrel" ? "US$ per barrel" : "US$ per tonne"} (toggle below · 6.35 bbl/tonne). Dotted = Singapore forward curve (17 Sep 2026)`}
      source="Source: Alphaliner Monthly Monitor, Sep 2026 (actuals) · Singapore VLSFO forward curve, 17 Sep 2026 (forecast) — Rotterdam & Houston cross-checked vs ShipandBunker / BunkerIndex"
      testid="chart-bunker"
      csv={csv}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50 no-print">
          <button
            data-testid="bunker-unit-barrel"
            onClick={() => setUnit("barrel")}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${unit === "barrel" ? "bg-primary text-white" : "text-slate-500"}`}
          >
            US$ / barrel
          </button>
          <button
            data-testid="bunker-unit-tonne"
            onClick={() => setUnit("tonne")}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${unit === "tonne" ? "bg-primary text-white" : "text-slate-500"}`}
          >
            US$ / tonne
          </button>
        </div>
        <span className="mx-1 h-4 w-px bg-slate-200 hidden sm:inline-block" />
        {PORTS.map((p) => (
          <button
            key={p.key}
            data-testid={`bunker-toggle-${p.key.toLowerCase()}`}
            onClick={() => setVisible((v) => ({ ...v, [p.key]: !v[p.key] }))}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${visible[p.key] ? "border-slate-300 bg-white text-slate-700" : "border-slate-200 bg-slate-50 text-slate-400"}`}
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: visible[p.key] ? p.color : "#cbd5e1" }} />
            {p.label}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 18 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} interval={0} height={44} tick={<MonthYearTick />} />
          <YAxis
            tickFormatter={(v) => `$${Math.round(v)}`}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#64748b" }}
            width={52}
            label={{ value: unit === "barrel" ? "US$ / barrel" : "US$ / tonne", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11, style: { textAnchor: "middle" } }}
          />
          <Tooltip content={<ChartTip formatter={fmt} />} />
          {PORTS.map((p) =>
            visible[p.key] ? (
              <Line key={p.key} type="monotone" dataKey={p.key} name={p.label} stroke={p.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />
            ) : null
          )}
          {PORTS.map((p) =>
            visible[p.key] && hasForecast[p.key] ? (
              <Line key={`${p.key}F`} type="monotone" dataKey={`${p.key}F`} name={`${p.label} — forecast`} stroke={p.color} strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} connectNulls />
            ) : null
          )}
        </LineChart>
      </ResponsiveContainer>

      {sgpTable && Array.isArray(sgpTable.rows) && sgpTable.rows.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button
            data-testid="bunker-sgp-table-toggle"
            onClick={() => setShowSgp((s) => !s)}
            className="no-print inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <TableIcon className="w-4 h-4" />
            {showSgp ? "Hide" : "View"} Singapore monthly table (VLSFO · LSMGO · IFO380)
            <ChevronDown className={`w-4 h-4 transition-transform ${showSgp ? "rotate-180" : ""}`} />
          </button>
          {showSgp && (
            <div className="mt-3 overflow-x-auto" data-testid="bunker-sgp-table">
              {sgpTable.note && <p className="text-xs text-slate-500 mb-2 leading-relaxed">{sgpTable.note}</p>}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left font-num text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Month</th>
                    <th className="py-2 px-2 text-right">0.5% VLSFO</th>
                    <th className="py-2 px-2 text-right">MoM</th>
                    <th className="py-2 px-2 text-right">YoY</th>
                    <th className="py-2 px-2 text-right">LSMGO</th>
                    <th className="py-2 px-2 text-right">MoM</th>
                    <th className="py-2 px-2 text-right">YoY</th>
                    <th className="py-2 px-2 text-right">IFO380</th>
                    <th className="py-2 px-2 text-right">MoM</th>
                    <th className="py-2 pl-2 text-right">YoY</th>
                  </tr>
                </thead>
                <tbody>
                  {sgpTable.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bunker-sgp-row-${i}`}>
                      <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap">{r.month}</td>
                      <td className="py-1.5 px-2 text-right font-num text-slate-900">${r.vlsfo}</td>
                      <td className={`py-1.5 px-2 text-right font-num ${String(r.vlsfo_mom).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.vlsfo_mom}</td>
                      <td className={`py-1.5 px-2 text-right font-num ${String(r.vlsfo_yoy).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.vlsfo_yoy}</td>
                      <td className="py-1.5 px-2 text-right font-num text-slate-900">${r.lsmgo}</td>
                      <td className={`py-1.5 px-2 text-right font-num ${String(r.lsmgo_mom).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.lsmgo_mom}</td>
                      <td className={`py-1.5 px-2 text-right font-num ${String(r.lsmgo_yoy).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.lsmgo_yoy}</td>
                      <td className="py-1.5 px-2 text-right font-num text-slate-900">${r.ifo380}</td>
                      <td className={`py-1.5 px-2 text-right font-num ${String(r.ifo380_mom).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.ifo380_mom}</td>
                      <td className={`py-1.5 pl-2 text-right font-num ${String(r.ifo380_yoy).startsWith("-") ? "text-orange-600" : "text-emerald-600"}`}>{r.ifo380_yoy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sgpTable.source && <p className="font-num text-[10px] uppercase tracking-wider text-slate-400 mt-3">{sgpTable.source}</p>}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
